import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/features/auth/AuthProvider'
import { useIsAdmin } from '@/features/auth/useIsAdmin'
import { useAuthorStatus } from '@/features/auth/useAuthorStatus'
import AcademyLogo from './AcademyLogo'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from '@teach/ui'
import { UserProfileDropdown } from './sidebar/UserProfileDropdown'
import { ReportBugSidebarButton } from '@/features/support/ReportBugButton'
import { MyCoursesGroup } from './sidebar/MyCoursesGroup'
import { ResourcesGroup } from './sidebar/ResourcesGroup'
import { useSidebarData } from './sidebar/useSidebarData'

const AppSidebar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuth()
  const m = useMessages(messages)
  const { setOpenMobile, isMobile } = useSidebar()
  const { userName, userAvatar, initials, recentCourses } = useSidebarData()
  const { isAdmin } = useIsAdmin()
  const { isAuthor, isVerified } = useAuthorStatus()

  // Любой переход (пункты меню профиля — «Настройки», «Баланс» — и т.д.) закрывает
  // шторку меню на телефоне и планшете: иначе она оставалась поверх новой страницы.
  useEffect(() => {
    if (isMobile) setOpenMobile(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- реагируем только на смену адреса
  }, [location.pathname])

  const handleNavigate = (path: string) => {
    navigate(path)
    if (isMobile) setOpenMobile(false)
  }

  const handleSignOut = async () => {
    await logout()
    toast.success(m.signedOut)
    navigate('/auth')
  }

  const isActive = (path: string) => location.pathname === path
  const isEditorRoute = (courseId: number) => location.pathname === `/editor/${courseId}`

  return (
    <Sidebar variant="floating">
      <SidebarHeader className="px-4 pt-7 pb-[26px] flex flex-row items-center justify-between">
        <AcademyLogo className="h-[1.2rem]" />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <UserProfileDropdown
                userName={userName}
                userAvatar={userAvatar}
                initials={initials}
                isAuthor={isAuthor}
                isVerified={isVerified}
                onSignOut={handleSignOut}
              />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        <MyCoursesGroup
          isActive={isActive}
          handleNavigate={handleNavigate}
          recentCourses={recentCourses}
          isEditorRoute={isEditorRoute}
        />
        <ResourcesGroup isActive={isActive} onNavigate={handleNavigate} />
        {isAdmin ? (
          <SidebarGroup>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive('/admin/panel')}
                  onClick={() => handleNavigate('/admin/panel')}
                >
                  <Shield className="size-[17px]" />
                  <span>{m.moderation}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        ) : null}
      </SidebarContent>
      {/* Внизу слева — «Сообщить о баге» (и в выезжающем сайдбаре телефона). */}
      <SidebarFooter className="px-2 pb-4">
        <ReportBugSidebarButton />
      </SidebarFooter>
    </Sidebar>
  )
}

export default AppSidebar
